import { FC, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import useTheme from '../../utils/useTheme';
import { IconButton } from '../IconButton/IconButton';
import { Tooltip } from '../Tooltip/Tooltip';
import { LabelTooltip } from './LabelTooltip';
import { SvgIcon } from '../SvgIcons/svgIcons';

const ContainerElement = styled.div<Pick<IProps, 'isBold'>>`
  display: flex;
  align-items: center;

  &.form {
    label {
      font-size: 14px;
      font-weight: ${({ isBold }) => (isBold ? '600' : '500')};
      color: ${props => (props.color ? props.color : props.theme.vars.newColor.darkGS1)};
      line-break: anywhere;
    }
  }
`;

const LabelElement = styled.label<Pick<IProps, 'isBold'>>`
  font-size: 14px;
  font-weight: ${({ isBold }) => (isBold ? '600' : '500')};
  color: ${(props: any) =>
    props.disabled
      ? props.theme.vars.newColor.darkGS3 + ' !important'
      : props.color
      ? props.color
      : props.theme.vars.newColor.darkGS1};
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  margin-bottom: ${(props: any) => (props.marginBottom ? '5px' : '0px')};
  margin-right: ${(props: any) => (props.marginRight ? '5px' : '0px')};
` as any;

const LabelIcons = styled.div<Pick<IProps, 'type'> & { compactLeadingSpacing?: boolean }>`
  display: flex;
  align-items: center;

  > * {
    margin-left: ${({ type, compactLeadingSpacing }) =>
      compactLeadingSpacing || type === 'number' || type === 'select' ? '0.5rem' : '1.5rem'};
  }
`;

export type LabelVariation = 'form';

export interface IProps extends IntrinsicProps {
  /** Name of the label */
  name: string;
  /** Specifies which form element a label is bound to */
  for?: string;
  /** Variation styles (default) | form  */
  variation?: LabelVariation;
  /** Event invoked when the label component is clicked. */
  onClick?: (e: any) => void;
  /** A hint can be passed as HTML to the label */
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string | string[];
  /** Optional tooltip description list. Deafults to undefined */
  tooltipContent?: JSX.Element;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /** Optional tooltip to display. This replaces the existing tooltip component. */
  tooltipComponent?: ReactNode;
  /** Adds a disabled style */
  disabled?: boolean;
  /** Optional margin bottom. Defaults to false */
  marginBottom?: boolean;
  /** Optional margin right. Defaults to false */
  marginRight?: boolean;
  /** Changes the color of the label */
  color?: string;
  /** Is the text bold? */
  isBold?: boolean;
  /** Show the password toggle icon */
  onPasswordToggleClick?: (e: React.MouseEvent) => void;
  /** Label value for the password toggle button. Default: 'Toggle password visibility' */
  passwordToggleLabel?: string;
  /** Is the password currently visible */
  passwordVisible?: boolean;
  width?: number | string;
  type?: string;
}

export const Label: FC<IProps> = ({
  color,
  disabled,
  for: htmlFor,
  hint,
  isBold,
  marginBottom,
  marginRight,
  name,
  onClick,
  tooltipContent,
  tooltipDescription,
  tooltipHeading,
  tooltipWidth,
  tooltipComponent,
  variation,
  onPasswordToggleClick,
  passwordVisible,
  passwordToggleLabel = 'Toggle password visibility',
  width,
  type,
  ...props
}) => {
  const theme = useTheme();

  const renderTooltip = useMemo(() => {
    const renderTooltipHtml = () => {
      if (tooltipHeading) {
        return (
          <LabelTooltip
            heading={tooltipHeading}
            description={tooltipDescription}
            content={tooltipContent}
            width={tooltipWidth}
          />
        );
      } else {
        return hint;
      }
    };

    if (tooltipComponent) {
      return <>{tooltipComponent}</>;
    }

    if (hint || tooltipHeading) {
      return (
        <Tooltip html={renderTooltipHtml()}>
          <SvgIcon name="circle-question" size="16" color={theme.designToken.neutral700} />
        </Tooltip>
      );
    }
  }, [
    hint,
    theme.designToken.neutral700,
    tooltipComponent,
    tooltipContent,
    tooltipDescription,
    tooltipHeading,
    tooltipWidth,
  ]);

  return (
    <ContainerElement className={variation} color={color} isBold={isBold} style={{ width }}>
      <LabelElement
        htmlFor={htmlFor}
        data-testid="Label"
        onClick={onClick}
        disabled={disabled}
        marginRight={marginRight || null}
        marginBottom={marginBottom || null}
        color={color}
        isBold={isBold}
        {...props}
      >
        {name}
      </LabelElement>

      <LabelIcons type={type} compactLeadingSpacing={Boolean(tooltipComponent)}>
        {!disabled && onPasswordToggleClick && passwordToggleLabel && (
          <IconButton
            ariaLabel={passwordToggleLabel}
            icon={passwordVisible ? 'eye-slash' : 'eye'}
            color={theme.vars.newColor.darkGS2}
            onClick={onPasswordToggleClick}
          />
        )}

        {renderTooltip}
      </LabelIcons>
    </ContainerElement>
  );
};
